import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LevelData, AdditionFact } from '../data/curriculum';
import { useTTS } from '../hooks/useTTS';
import { factToQuestionSpeechText, factToSpeechText } from '../data/audioTextGenerator';
import FactDisplay from '../components/FactDisplay';
import AnswerInput from '../components/AnswerInput';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';
import { motion, AnimatePresence } from 'framer-motion';
import { playSuccessSound } from '../utils/sounds';

interface Step5CalculatorInputProps {
  level: LevelData;
  onComplete: () => void;
}

const Step5CalculatorInput: React.FC<Step5CalculatorInputProps> = ({ level, onComplete }) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [retryFact, setRetryFact] = useState<AdditionFact | null>(null);
  const { playText, isLoading, isPlaying, error: ttsError } = useTTS();
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentFact = level.primaryFacts[currentFactIndex];
  const displayedFact = retryFact || currentFact;

  // Play question audio when moving to a new fact (skip in retry mode — student just heard the answer)
  useEffect(() => {
    if (!retryFact) {
      playText(factToQuestionSpeechText(displayedFact));
    }
  }, [currentFactIndex, retryFact]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  const handleSubmit = useCallback(
    (answer: number) => {
      if (isAdvancing) return;

      if (answer === displayedFact.sum) {
        playSuccessSound();
        setFeedback('correct');
        setIsAdvancing(true);

        advanceTimerRef.current = setTimeout(() => {
          setFeedback(null);
          setIsAdvancing(false);
          setRetryFact(null);

          if (currentFactIndex < level.primaryFacts.length - 1) {
            setCurrentFactIndex((prev) => prev + 1);
          } else {
            onComplete();
          }
        }, 800);
      } else {
        setFeedback('incorrect');
        setIsAdvancing(true);
        playText(factToSpeechText(displayedFact));

        feedbackTimerRef.current = setTimeout(() => {
          setFeedback(null);
          setIsAdvancing(false);
          setRetryFact(displayedFact);
        }, 1500);
      }
    },
    [displayedFact, currentFactIndex, level.primaryFacts.length, onComplete, isAdvancing, retryFact]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full"
    >

      <div className="mb-6">
        {retryFact ? (
          <FactDisplay fact={displayedFact} showAnswer={true} size="large" />
        ) : (
          <FactDisplay fact={displayedFact} showAnswer={false} size="large" />
        )}
      </div>

      <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />

      <AnimatePresence mode="wait">
        {feedback === 'correct' && (
          <motion.div
            key="correct"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-green-500 font-bold text-lg mb-4"
          >
            Correct!
          </motion.div>
        )}
        {feedback === 'incorrect' && (
          <motion.div
            key="incorrect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-red-500 font-bold text-lg mb-4"
          >
            The answer is {displayedFact.sum}
          </motion.div>
        )}
        {feedback === null && <div className="h-[28px] mb-4" />}
      </AnimatePresence>

      <AnswerInput
        answer={displayedFact.sum}
        onSubmit={handleSubmit}
        disabled={isAdvancing}
        feedback={feedback}
        resetKey={retryFact ? `retry_${currentFactIndex}` : currentFactIndex}
      />
    </motion.div>
  );
};

export default Step5CalculatorInput;
